<template>
  <div class="bch-header">
    <div class="bch-text">
      <div class="bch-title">{{ summary.name }}</div>
      <div class="bch-subtitle">{{ appliedText }}</div>
    </div>
    <FilterPopover
      title="Filter"
      popoverWidth="380px"
      :activeCount="activeCount"
      @open="onOpenFilter"
      @apply="onApplyFilter"
      @clear="onClearFilter"
    >
      <div class="col-12">
        <label class="filter-label">Master Budget Line</label>
        <v-select
          v-model="draft.line"
          class="bch-select"
          placeholder="Select master budget line"
          :options="lineOptions"
          label="name"
          :reduce="option => option.id"
          :clearable="false"
          @update:modelValue="onSelectLine"
        />
      </div>
      <div class="col-12">
        <label class="filter-label">Budget Allocation</label>
        <v-select
          v-model="draft.budget"
          class="bch-select"
          placeholder="All approved budget allocations"
          :options="budgetOptions"
          label="label"
          :reduce="option => option.id"
        />
      </div>
      <div v-if="draftHeadTypes.length" class="col-12">
        <label class="filter-label">Head Type</label>
        <v-select
          v-model="draft.head_type"
          class="bch-select"
          placeholder="Head type"
          :options="draftHeadTypes"
          label="name"
          :reduce="option => option.id"
          :clearable="false"
          :searchable="false"
        />
      </div>
      <div v-if="limitOptions.length" class="col-12">
        <label class="filter-label">Show</label>
        <v-select
          v-model="draft.limit"
          class="bch-select"
          placeholder="Show"
          :options="limitOptions"
          label="name"
          :reduce="option => option.id"
          :clearable="false"
          :searchable="false"
        />
      </div>
    </FilterPopover>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import useDate from '@/services/utils/day'

const DEFAULT_LIMIT = 5

const props = defineProps({
  summary: { type: Object, required: true }
})

const emit = defineEmits(['update:scope', 'update:limit', 'update:headType'])

const { formatToDdMmmYyyy } = useDate()

const lines = computed(() => props.summary.lines || {})

const lineOptions = computed(() => Object.keys(lines.value)
  .map(id => ({ id: id, name: lines.value[id].name }))
  .sort((first, second) => first.name.localeCompare(second.name)))

const defaultLine = computed(() => {
  if (props.summary.default_line) return props.summary.default_line
  return lineOptions.value.length ? lineOptions.value[0].id : null
})

const lineOf = (key) => lines.value[key] || lines.value[defaultLine.value] || null

const budgetsOf = (line) => {
  if (! line) return {}
  return line.budgets || {}
}

const scopeOf = (filters) => {
  const line = lineOf(filters.line)
  const budget = budgetsOf(line)[filters.budget]
  if (budget) return budget
  return line ? line.all : null
}

const headTypeOptions = computed(() => props.summary.head_types || [])

const hasHeadValues = (head) => {
  if (typeof head === 'number') return head !== 0
  if (! head || typeof head !== 'object') return false
  return Object.values(head).some(hasHeadValues)
}

const headTypesOf = (filters) => {
  const scope = scopeOf(filters)
  if (! scope || ! scope.heads) return []
  return headTypeOptions.value.filter(option => hasHeadValues(scope.heads[option.id]))
}

const presentHeadType = (filters) => {
  const scope = scopeOf(filters)
  if (! scope || ! scope.heads) return null
  const option = headTypeOptions.value.find(item => scope.heads[item.id])
  return option ? option.id : null
}

const firstHeadType = (filters) => {
  const options = headTypesOf(filters)
  return options.length ? options[0].id : presentHeadType(filters)
}

const validHeadType = (filters) => {
  const isValid = headTypesOf(filters).some(option => option.id === filters.head_type)
  return isValid ? filters.head_type : firstHeadType(filters)
}

const defaultFilters = () => {
  const filters = { line: defaultLine.value, budget: null, limit: DEFAULT_LIMIT }
  return { ...filters, head_type: firstHeadType(filters) }
}

const applied = ref(defaultFilters())
const draft = ref(defaultFilters())

const currentLine = computed(() => lineOf(applied.value.line))
const currentBudget = computed(() => budgetsOf(currentLine.value)[applied.value.budget] || null)

const currentScope = computed(() => scopeOf(applied.value))

const budgetOptions = computed(() => {
  const budgets = budgetsOf(lineOf(draft.value.line))
  return Object.keys(budgets)
    .map(id => ({ id: id, label: budgets[id].label }))
    .sort((first, second) => first.label.localeCompare(second.label))
})

const draftHeadTypes = computed(() => headTypesOf(draft.value))

const appliedHeadType = computed(() => headTypeOptions.value.find(option => option.id === applied.value.head_type) || null)

const limitOptions = computed(() => {
  const limits = props.summary.top_limits || {}
  return Object.keys(limits)
    .map(id => ({ id: Number(id), name: limits[id] }))
    .sort((first, second) => first.id - second.id)
})

const activeCount = computed(() => {
  const defaults = { ...defaultFilters(), head_type: firstHeadType(applied.value) }
  return ['line', 'budget', 'head_type', 'limit'].filter(key => applied.value[key] !== defaults[key]).length
})

const appliedText = computed(() => {
  const parts = []
  if (currentLine.value) parts.push(currentLine.value.name)
  if (currentLine.value) parts.push(currentBudget.value ? currentBudget.value.label : 'All allocations')
  if (appliedHeadType.value) parts.push(appliedHeadType.value.name)
  if (props.summary.as_of) parts.push(`As of ${formatToDdMmmYyyy(props.summary.as_of)}`)
  return parts.join(' · ')
})

const onSelectLine = () => {
  draft.value.budget = null
}

const onOpenFilter = () => {
  draft.value = { ...applied.value }
}

const onApplyFilter = () => {
  applied.value = { ...draft.value }
}

const onClearFilter = () => {
  applied.value = defaultFilters()
  draft.value = defaultFilters()
}

watch(defaultLine, onClearFilter)
watch(() => scopeOf(draft.value), () => {
  draft.value.head_type = validHeadType(draft.value)
})
watch(currentScope, () => {
  applied.value.head_type = validHeadType(applied.value)
})
watch(currentScope, value => emit('update:scope', value), { immediate: true })
watch(() => applied.value.limit, value => emit('update:limit', value), { immediate: true })
watch(() => applied.value.head_type, value => emit('update:headType', value), { immediate: true })
</script>

<style>
section.dashboard-section:has(.bch-header) {
  overflow: visible;
}
</style>

<style scoped>
.bch-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f0f1f4;
}
.bch-text {
  flex: 1 1 auto;
  min-width: 0;
}
.bch-title {
  font-size: 15px;
  font-weight: 700;
  color: #2b2b2b;
}
.bch-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: #8a8894;
}
.bch-select :deep(.vs__selected-options) {
  flex-wrap: nowrap;
  min-width: 0;
}
.bch-select :deep(.vs__selected) {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.bch-select :deep(.vs__dropdown-option) {
  white-space: normal;
}
@media (max-width: 576px) {
  .bch-header {
    position: relative;
  }
  .bch-header :deep(.filter-pop-wrap) {
    position: static;
  }
  .bch-header :deep(.filter-pop) {
    left: 0;
    right: 0;
    width: 100% !important;
  }
}
</style>
